import { useEffect, useRef, useState } from 'react';

interface ServiceWorkerState {
  waiting: boolean;
  updateAvailable: boolean;
  registration: ServiceWorkerRegistration | null;
  updateServiceWorker: () => void;
}

export const useServiceWorker = (): ServiceWorkerState => {
  const [state, setState] = useState<ServiceWorkerState>({
    waiting: false,
    updateAvailable: false,
    registration: null,
    updateServiceWorker: () => {},
  });

  const swRef = useRef<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      registerSW();
    }
  }, []);

  const registerSW = async (): Promise<void> => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      });

      swRef.current = registration;
      await registration.update();

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              setState((prev) => ({
                ...prev,
                waiting: true,
                updateAvailable: true,
                registration,
              }));
            }
          });
        }
      });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });

      setState((prev) => ({ ...prev, registration }));
    } catch (error) {
      console.error('SW registration failed:', error);
    }
  };

  const updateServiceWorker = (): void => {
    const reg = swRef.current || state.registration;
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  return { ...state, updateServiceWorker };
};
